<!-- @format -->
<template>
    <div class="appointment-request-details-widget">
        <div class="widget-header">
            <h3>Review</h3>
            <button class="widget-close-button" @click="close">
                <widget-close-svg />
            </button>
        </div>

        <div class="content-wrapper">
            <div v-if="isLoading" class="loading-container">
                <loading-screen-svg />
            </div>
            <div v-else class="details-container">
                <h3>Appointment Generation Details</h3>
                <appointment-request-details-generation
                    :appointment-request="appointmentRequest"
                    :finance-values="financeValues"
                    :subtotal="subtotal"
                />

                <div class="divider"></div>

                <h3>Appointment Availability</h3>
                <appointment-request-details-availability
                    :appointment-request="computedAppointmentRequest"
                />

                <div class="divider"></div>

                <appointment-request-details-lists :appointment-request="appointmentRequest" />

                <appointment-request-disclaimer />
            </div>
        </div>

        <div class="widget-footer">
            <app-button class="cancel-button back-button" @click="close"> Close </app-button>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus';
import { APPOINTMENT_REQUEST_DETAILS_WIDGET_NAME } from '@/components/sliding-widgets/appointment-request-details-widget/appointment-request-details-widget';
import WidgetCloseSvg from '@/components/svg/quick-add/widget-close-svg.vue';
import { AppointmentRequestService } from '@/scripts/services/appointment-request/appointment-request-service';

const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg.vue');
const AppButton = () => import('@/components/common/app-button.vue');
const AppointmentRequestDetailsGeneration = () =>
    import('@/components/appointment-request/appointment-request-details-generation.vue');
const AppointmentRequestDetailsLists = () =>
    import(
        '@/components/sliding-widgets/appointment-request-details-widget/appointment-request-details-lists.vue'
    );
const AppointmentRequestDetailsAvailability = () =>
    import('@/components/appointment-request/appointment-request-details-availability.vue');
const AppointmentRequestDisclaimer = () =>
    import('@/components/appointment-request/appointment-request-disclaimer.vue');

export default {
    name: APPOINTMENT_REQUEST_DETAILS_WIDGET_NAME,

    components: {
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'app-button': AppButton,
        'appointment-request-details-generation': AppointmentRequestDetailsGeneration,
        'appointment-request-details-availability': AppointmentRequestDetailsAvailability,
        'appointment-request-details-lists': AppointmentRequestDetailsLists,
        'appointment-request-disclaimer': AppointmentRequestDisclaimer,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            appointmentRequest: null,
        };
    },

    computed: {
        clientId() {
            return this.$store.state.context.client.id;
        },

        financeValues() {
            return {
                pricePerAppointmentUSD: this.appointmentRequest.pricePerAppointmentUSD,
                feePercentage: this.appointmentRequest.feePercentage,
            };
        },

        subtotal() {
            return this.appointmentRequest.quantity * this.financeValues.pricePerAppointmentUSD;
        },

        computedAppointmentRequest() {
            return {
                ...this.appointmentRequest,
                timezone: this.formatTimezone(),
            };
        },
    },

    async created() {
        this.isLoading = true;

        try {
            await this.loadAppointmentRequest();
        } catch (error) {
            this.$helpers.showFailNotification('Failed to load appointment request details', {
                error,
            });

            this.close();
        } finally {
            this.isLoading = false;
        }
    },

    methods: {
        async loadAppointmentRequest() {
            const id = this.widget.config.id;

            return AppointmentRequestService.get(id)
                .then((res) => {
                    this.appointmentRequest = res.data.data;

                    return this.appointmentRequest.excludedContactLists.map((id) => id);
                })
                .then((ids) => {
                    return this.loadExcludedContactLists(ids);
                });
        },

        async loadExcludedContactLists(ids) {
            return AppointmentRequestService.getContactLists(this.clientId, {
                ids,
            }).then((res) => {
                this.appointmentRequest.excludedContactLists = (res.data.value ?? []).map(
                    (list) => ({
                        id: list.Id,
                        label: `${list.Name} (${list.SubscriberCount})`,
                    }),
                );
            });
        },

        close() {
            EventBus.$emit('close-widget', this.widget);
        },

        formatTimezone() {
            if (!this.appointmentRequest) {
                return 'Invalid timezone';
            }

            const { timezone, timezoneOffset } = this.appointmentRequest;

            if (!this.$moment.tz.zone(timezone)) {
                return 'Invalid timezone';
            }
            const nowInTimezone = this.$moment.tz(new Date(), timezone);
            const gmtOffset = nowInTimezone.format('Z');

            if (gmtOffset !== timezoneOffset) {
                console.warn(
                    `Provided offset (${timezoneOffset}) does not match the timezone offset (${gmtOffset})`,
                );
                return 'Invalid timezone';
            }

            return `GMT(${timezoneOffset}) ${timezone}`;
        },
    },
};
</script>

<style>
.appointment-request-details-widget .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
}

.appointment-request-details-widget .divider {
    margin: 24px 0;
    border-bottom: 1px solid var(--color-border-divider);
}

.appointment-request-details-widget .details-container h3 {
    margin-bottom: 24px;
}

.appointment-request-details-widget .appointment-request-disclaimer {
    margin-top: 28px;
}
</style>
